/**
 * ⚠️ ESTIMATION SEULEMENT — PAS UNE SOURCE DE CONFORMITÉ FISCALE.
 *
 * La Floride n'a pas d'impôt sur le revenu au niveau de l'État — seules les
 * retenues fédérales s'appliquent. Les tranches d'impôt fédéral ci-dessous
 * sont une approximation simplifiée (méthode par pourcentage, déclarant
 * célibataire) et doivent être vérifiées/mises à jour chaque année contre
 * la Publication 15-T de l'IRS avant tout usage réel de versement de paie.
 * Ne PAS utiliser ces chiffres pour émettre un talon de paie officiel sans
 * validation par un comptable ou un service de paie agréé (Gusto, Check,
 * Zeal, ADP...).
 */

const SOCIAL_SECURITY_RATE = 0.062;
const MEDICARE_RATE = 0.0145;
const ADDITIONAL_MEDICARE_RATE = 0.009;
const ADDITIONAL_MEDICARE_ANNUAL_THRESHOLD = 200_000;

// Tranches fédérales annualisées, approximation célibataire — À REVALIDER.
function estimateFederalIncomeTaxWeekly(weeklyGross: number): number {
  const annualized = weeklyGross * 52;
  let tax: number;
  if (annualized <= 11_600) tax = annualized * 0.10;
  else if (annualized <= 47_150) tax = 1_160 + (annualized - 11_600) * 0.12;
  else if (annualized <= 100_525) tax = 5_426 + (annualized - 47_150) * 0.22;
  else if (annualized <= 191_950) tax = 17_168.5 + (annualized - 100_525) * 0.24;
  else tax = 39_110.5 + (annualized - 191_950) * 0.32;
  return tax / 52;
}

export interface PayrollEstimateInput {
  regularHours: number;
  overtimeHours: number;
  hourlyRate: number;
  bonus: number;
  ytdGrossBeforeThisWeek?: number; // pour le seuil Medicare additionnel
}

export interface PayrollEstimate {
  regularPay: number;
  overtimePay: number;
  bonus: number;
  grossPay: number;
  deductions: {
    federalIncomeTax: number;
    socialSecurity: number;
    medicare: number;
    additionalMedicare: number;
    total: number;
  };
  netPay: number;
}

export function estimatePayroll({
  regularHours,
  overtimeHours,
  hourlyRate,
  bonus,
  ytdGrossBeforeThisWeek = 0,
}: PayrollEstimateInput): PayrollEstimate {
  const regularPay = regularHours * hourlyRate;
  const overtimePay = overtimeHours * hourlyRate * 1.5;
  const grossPay = regularPay + overtimePay + bonus;

  const federalIncomeTax = estimateFederalIncomeTaxWeekly(grossPay);
  const socialSecurity = grossPay * SOCIAL_SECURITY_RATE;
  const medicare = grossPay * MEDICARE_RATE;

  const ytdAfterThisWeek = ytdGrossBeforeThisWeek + grossPay;
  const additionalMedicare =
    ytdAfterThisWeek > ADDITIONAL_MEDICARE_ANNUAL_THRESHOLD
      ? Math.min(grossPay, ytdAfterThisWeek - ADDITIONAL_MEDICARE_ANNUAL_THRESHOLD) * ADDITIONAL_MEDICARE_RATE
      : 0;

  const totalDeductions = federalIncomeTax + socialSecurity + medicare + additionalMedicare;

  return {
    regularPay,
    overtimePay,
    bonus,
    grossPay,
    deductions: {
      federalIncomeTax,
      socialSecurity,
      medicare,
      additionalMedicare,
      total: totalDeductions,
    },
    netPay: grossPay - totalDeductions,
  };
}